import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  EmployeeAddress,
  HrmsEmployee,
  HrmsEmployeeListParams,
} from "../../../models/Employees/HrmsEmployee";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../components/common/pages/AppTableRowCell";
import { formatDateWithoutTime } from "../../components/common/functions/formatDate";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import AppDropDown from "../../components/common/filter/AppDropDown";
import CustomTypography from "../../components/common/form/CustomTypography";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "../../components/common/hooks/Debounce";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import Grid2 from "@mui/material/Grid2";

const HrmsList: React.FC = () => {
  const { hrmsStore, userStore } = useStores();
  const theme = useTheme();
  type HrmsStatusTab = "ACTIVE" | "INACTIVE" | "NEW";
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const resolvedCustomerId = (() => {
    const raw = searchParams.get("customer_id");
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  })();

  const initialStatusTab = ((): HrmsStatusTab => {
    if (searchParams.get("new_employees") === "true") return "NEW";
    if (
      searchParams.get("employment_status") === "INACTIVE" ||
      searchParams.get("inactive") === "true"
    ) {
      return "INACTIVE";
    }
    return "ACTIVE";
  })();

  const [params, setParams] = useState<HrmsEmployeeListParams>({
    customer_id: resolvedCustomerId ?? 0,
    page_no: searchParams.get("page_no")
      ? parseInt(searchParams.get("page_no") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    search_text: searchParams.get("search_text") || "",
    employment_status:
      searchParams.get("employment_status") === "INACTIVE"
        ? "INACTIVE"
        : searchParams.get("new_employees") === "true"
          ? undefined
          : "ACTIVE",
    active:
      searchParams.get("active") === "true"
        ? true
        : searchParams.get("inactive") === "true" ||
            searchParams.get("new_employees") === "true"
          ? undefined
          : true,
    inactive: searchParams.get("inactive") === "true" ? true : undefined,
    new_employees:
      searchParams.get("new_employees") === "true" ? true : undefined,
    new_days: searchParams.get("new_days")
      ? parseInt(searchParams.get("new_days") as string, 10)
      : undefined,
  });
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search_text") || "",
  );
  const debouncedSearchInput = useDebounce(searchInput, 500);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [selectedStatusTab, setSelectedStatusTab] =
    useState<HrmsStatusTab>(initialStatusTab);

  const HRMS_STATUS_TABS: { id: HrmsStatusTab; label: string }[] = [
    { id: "ACTIVE", label: "Active" },
    { id: "INACTIVE", label: "Inactive" },
    { id: "NEW", label: "New" },
  ];

  const NEW_DAY_OPTIONS: FilterValue[] = [
    { id: "30", value: "30 days (Default)" },
    { id: "60", value: "60 days" },
    { id: "90", value: "90 days" },
    { id: "120", value: "120 days" },
  ];

  const canViewHrms = userStore.hasPermission([EmployeeRoles.VIEW_HRMS]);

  useEffect(() => {
    if (!canViewHrms) return;
    hrmsStore.getEmployees(params).catch(() => {});
  }, [hrmsStore, params, canViewHrms]);

  useEffect(() => {
    // Counts are global; endpoint uses backend default new_days.
    if (!canViewHrms) return;
    hrmsStore.getEmployeeCounts().catch(() => {
      // Keep list experience functional even if counts endpoint fails.
    });
  }, [hrmsStore, canViewHrms]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (params.customer_id > 0) {
      newSearchParams.set("customer_id", String(params.customer_id));
    }
    newSearchParams.set("page_no", String(params.page_no));
    newSearchParams.set("limit", String(params.limit));
    if (params.search_text) newSearchParams.set("search_text", params.search_text);
    if (params.employment_status) {
      newSearchParams.set("employment_status", params.employment_status);
    }
    if (params.active === true) newSearchParams.set("active", "true");
    if (params.inactive === true) newSearchParams.set("inactive", "true");
    if (params.new_employees === true) newSearchParams.set("new_employees", "true");
    if (params.new_days !== undefined) {
      newSearchParams.set("new_days", String(params.new_days));
    }

    navigate({ search: newSearchParams.toString() }, { replace: true });
  }, [params, navigate]);

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      page_no: 1,
      search_text: searchInput.trim(),
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  useEffect(() => {
    const newSearch = debouncedSearchInput.trim();
    setParams((prev) => {
      if ((prev.search_text || "") === newSearch) return prev;
      return {
        ...prev,
        page_no: 1,
        search_text: newSearch,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const handleStatusFilter = (status: HrmsStatusTab) => {
    setSelectedStatusTab(status);
    setParams((prev) => {
      if (status === "INACTIVE") {
        return {
          ...prev,
          page_no: 1,
          employment_status: "INACTIVE",
          active: undefined,
          inactive: true,
          new_employees: undefined,
          new_days: undefined,
        };
      }
      if (status === "NEW") {
        return {
          ...prev,
          page_no: 1,
          employment_status: undefined,
          active: undefined,
          inactive: undefined,
          new_employees: true,
          new_days: undefined, // default is 30 days when new_days is omitted
        };
      }
      return {
        ...prev,
        page_no: 1,
        employment_status: "ACTIVE",
        active: true,
        inactive: undefined,
        new_employees: undefined,
        new_days: undefined,
      };
    });
  };

  const getPhoneNumber = (employee: HrmsEmployee) =>
    employee.phone_numbers?.[0]?.number || "-";
  const getDisplayName = (employee: HrmsEmployee) =>
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim() || "-";
  const pickAddress = (
    addresses?: EmployeeAddress[] | null,
  ): EmployeeAddress | null => {
    if (!addresses?.length) return null;
    return (
      addresses.find((address) => address.address_type === "HOME_PRESENT") ||
      addresses.find((address) => address.address_type === "HOME_PERMANENT") ||
      addresses[0]
    );
  };
  const formatAddress = (address?: EmployeeAddress | null): string => {
    if (!address) return "-";
    const parts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.state,
      address.country,
      address.zip_code,
    ]
      .filter((value) => value && String(value).trim() !== "")
      .map((value) => String(value).trim());
    return parts.length ? parts.join(", ") : "-";
  };

  if (!canViewHrms) {
    return <PermissionBlockLabel title="HRMS" />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <CustomTypography variant="heading2" fontWeight={700}>
              HRMS Employee Listing
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.text.secondary}
            >
              List of employees in the system.
            </CustomTypography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "nowrap",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 320 }}>
                    <FormTextField
                      id="hrms-search"
                      placeholder="Search by name / email"
                      type="search"
                      variant="outlined"
                      sx={{ mb: 0 }}
                      value={searchInput}
                      onChange={(_, value) => setSearchInput(value || "")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearch}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 260  ,padding: 2}}>
                <AppDropDown
                  title="New Employees in Last"
                  isMultiSelect={false}
                  value={String(params.new_days ?? 30)}
                  list={NEW_DAY_OPTIONS}
                  sx={{ width: 260 }}
                  onSingleSelectionChange={(id) => {
                    const parsed = parseInt(String(id), 10);

                    setSelectedStatusTab("NEW");
                    setParams((prev) => ({
                      ...prev,
                      page_no: 1,
                      employment_status: undefined,
                      active: undefined,
                      inactive: undefined,
                      new_employees: true,
                      new_days: Number.isFinite(parsed) ? parsed : undefined,
                    }));
                  }}
                />
              </Box>
            </Collapse>
          </Paper>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mt: 1,
              mb: 1.5,
              px: 1,
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              Total Count:
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              ACTIVE: {hrmsStore.isLoadingCounts ? "..." : hrmsStore.counts.active}
            </CustomTypography>
            <CustomTypography variant="body2" color={theme.palette.text.disabled}>
              ·
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              INACTIVE: {hrmsStore.isLoadingCounts ? "..." : hrmsStore.counts.inactive}
            </CustomTypography>
            <CustomTypography variant="body2" color={theme.palette.text.disabled}>
              ·
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              NEW (Last 30 days): {hrmsStore.isLoadingCounts ? "..." : hrmsStore.counts.new_employees}
            </CustomTypography>
            <CustomTypography variant="body2" color={theme.palette.text.disabled}>
              ·
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={600}>
              TOTAL: {hrmsStore.isLoadingCounts ? "..." : hrmsStore.counts.total}
            </CustomTypography>
          </Box>

          <Tabs
            value={selectedStatusTab}
            onChange={(_, newValue: HrmsStatusTab) => {
              handleStatusFilter(newValue);
            }}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            }}
          >
            {HRMS_STATUS_TABS.map((tab) => (
              <Tab key={tab.id} value={tab.id} label={tab.label.toUpperCase()} />
            ))}
          </Tabs>

          {hrmsStore.errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hrmsStore.errorMessage}
            </Alert>
          )}

          {hrmsStore.isLoadingList ? (
            <Box
              sx={{
                minHeight: "35vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              sx={{
                marginTop: "0.6rem",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor ||
                          theme.palette.action.hover,
                        "& th": {
                          py: 1,
                          height: 48,
                        },
                      }}
                    >
                      <AppTableHeaderCell title="Emp No." sx={{ minWidth: 120 }} />
                      <AppTableHeaderCell
                        title="Name"
                        fontSize="14px"
                        sx={{ minWidth: 180, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Customer Name"
                        fontSize="14px"
                        sx={{ minWidth: 180, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Work Email"
                        fontSize="14px"
                        sx={{ minWidth: 220, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Phone"
                        fontSize="14px"
                        sx={{ minWidth: 140, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Address"
                        fontSize="14px"
                        sx={{ minWidth: 280, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Status"
                        fontSize="14px"
                        sx={{ minWidth: 120, textAlign: "center" }}
                      />
                      <AppTableHeaderCell
                        title="Start Date"
                        fontSize="14px"
                        sx={{ minWidth: 120, textAlign: "center" }}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hrmsStore.employees.map((employee) => (
                      <TableRow
                        key={employee.id}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          "& td": {
                            fontSize: "12px",
                            py: 1,
                            height: 48,
                            lineHeight: 1.2,
                          },
                        }}
                      >
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {employee.employee_number || "-"}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getDisplayName(employee)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {employee.customer_name || "-"}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {employee.work_email || "-"}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getPhoneNumber(employee)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {formatAddress(pickAddress(employee.addresses))}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          <Chip
                            size="small"
                            label={employee.employment_status || "-"}
                            color={
                              employee.employment_status === "ACTIVE"
                                ? "success"
                                : "default"
                            }
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {formatDateWithoutTime(employee.start_date)}
                        </AppTableRowCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {hrmsStore.employees.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    p: 4,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor:
                      theme.palette.customColors?.grayBackGroundColor ||
                      theme.palette.action.hover,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={600}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No employees found for selected filters.
                  </CustomTypography>
                </Box>
              )}
              <AppTablePagenation
                fontSize="12px"
                totalCount={hrmsStore.total_count}
                limit={params.limit}
                pageNo={params.page_no}
                onPageChange={handlePageChange}
                handleRowsChange={handleRowsChange}
              />
            </Paper>
          )}
        </Paper>
      </Box>
    </Grid2>
  );
};

export default observer(HrmsList);
